import { searchShowOnMapClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import TextLink from 'LuxKit/TextLink'
import LineMapMarkerIcon from 'LuxKit/Icons/line/LineMapMarkerIcon'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import Tooltip from 'LuxKit/Tooltip'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatInlineList from 'components/utils/Formatters/FormatInlineList'
import type { MouseEventHandler } from 'react'
import { useCallback, useContext } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import { trackClientEvent } from 'analytics/analytics'
import { pushWithRegion } from 'actions/NavigationActions'
import { useAppDispatch } from 'hooks/reduxHooks'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import GeoContext from 'contexts/geoContext'
import { getSearchDistanceLabel } from 'components/OfferList/OfferListTiles/Support/searchDistanceLabel'
import usePropertyNearby from 'hooks/Offers/usePropertyNearby'
import useExperiment from 'hooks/Optimizely/useExperiment'
import { Experiments } from 'constants/optimizely'

const NEARBY_POI_MAX_DISTANCE_M = 10_000
const NEARBY_PLACE_MAX_DISTANCE_M = 3_000
const BEACH_MAX_DISTANCE_M = 2_000
const BEACH_CLOSE_THRESHOLD_M = 500
const MAX_DISTANCE_LABELS = 2

interface Props {
  locations: Array<string>
  mapUrl?: string
  distance?: number
  searchTargetLabel?: string
  nearbyPois?: Array<App.NearbyPoi>
  propertyId?: string
  showDistanceLabels?: boolean
}

function SearchTileRowOverlineLocation({ locations, mapUrl, distance, searchTargetLabel, nearbyPois, propertyId, showDistanceLabels = true }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { isHomesAndVillasSplitMapVisible } = useContext(GlobalSearchStateContext)
  const { currentRegionCode } = useContext(GeoContext)
  const { isAnyVariant: nearbyEnabled } = useExperiment(
    Experiments.searchTilePropertyNearby,
    { canActivate: !!propertyId && showDistanceLabels },
  )
  const nearby = usePropertyNearby(propertyId, { disabled: !nearbyEnabled || !showDistanceLabels })

  const labels: Array<string> = []

  if (distance && searchTargetLabel) {
    labels.push(getSearchDistanceLabel(intl, searchTargetLabel, distance, currentRegionCode))
  }

  const centrePoi = nearbyPois?.[0]
  if (centrePoi && centrePoi.distance <= NEARBY_POI_MAX_DISTANCE_M) {
    const centreLabel = centrePoi.type === 'centre' ? 'centre' : centrePoi.name
    labels.push(getSearchDistanceLabel(intl, centreLabel, centrePoi.distance, currentRegionCode))
  }

  const beachPlace = nearby.beach?.[0]
  const trainPlace = nearby.train_station?.[0]

  const nearbyEntries: Array<{ distanceM: number; label: string }> = []

  if (beachPlace && beachPlace.distanceM <= BEACH_MAX_DISTANCE_M) {
    const beachLabel = beachPlace.distanceM <= BEACH_CLOSE_THRESHOLD_M
      ? intl.formatMessage({
          id: 'etULfhf5S0qwretX9nL7',
          defaultMessage: 'Close to beach',
          description: 'Label shown instead of a distance when a property is within 500m of a beach',
        })
      : getSearchDistanceLabel(
          intl,
          intl.formatMessage({
            id: '_hcoR94jl_CwnrTkTZ-c',
            defaultMessage: 'beach',
            description: 'Generic label for the closest beach near a property; shown instead of the specific beach name to avoid noisy POI names',
          }),
          beachPlace.distanceM,
          currentRegionCode,
        )
    nearbyEntries.push({ distanceM: beachPlace.distanceM, label: beachLabel })
  }

  if (trainPlace && trainPlace.distanceM <= NEARBY_PLACE_MAX_DISTANCE_M) {
    nearbyEntries.push({
      distanceM: trainPlace.distanceM,
      label: getSearchDistanceLabel(
        intl,
        intl.formatMessage({
          id: 'dkaV_RHvVkfOdzEt08TF',
          defaultMessage: 'train',
          description: 'Generic label for the closest train station near a property',
        }),
        trainPlace.distanceM,
        currentRegionCode,
      ),
    })
  }

  nearbyEntries.sort((a, b) => a.distanceM - b.distanceM)
  for (const { label } of nearbyEntries) {
    labels.push(label)
  }

  const distanceLabels = labels.slice(0, MAX_DISTANCE_LABELS)
  const distanceSeparator = ` ${intl.formatMessage(middleDotSeparatorMessage)} `

  const showOnMapLabel = intl.formatMessage({
    id: '9llOWZPByHFkZa27u2zT',
    defaultMessage: 'Show on map',
    description: 'Link text and aria-label to show hotel location on map in search tile',
  })
  const middleDotLabel = intl.formatMessage(middleDotSeparatorMessage)

  const mapViewClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.stopPropagation()
    event.preventDefault()

    trackClientEvent({
      subject: 'show_on_map',
      action: 'clicked',
      type: 'interaction',
      category: 'vertical_page',
    })

    addGTMEvent(searchShowOnMapClick())

    if (mapUrl) {
      let destinationUrl = mapUrl

      const isHomesAndVillasMapRoute = mapUrl.startsWith('/search/homes-and-villas/map?')

      if (isHomesAndVillasMapRoute && isHomesAndVillasSplitMapVisible) {
        destinationUrl = mapUrl.replace('/search/homes-and-villas/map?', '/search/homes-and-villas?')
      }

      dispatch(pushWithRegion(destinationUrl))
    }
  }, [mapUrl, dispatch, isHomesAndVillasSplitMapVisible])

  return <>
    <BodyText variant="small" colour="light-foreground-alt">
      <FormatInlineList list={locations} type="unit" />
      {!!mapUrl && (
        <FormattedMessage
          id="o2ZRAxWmdsD2Fj2ukIzq"
          defaultMessage="<separator>{middleDotLabel}</separator><link>{showOnMapLabel}</link>"
          description="Middle dot separator followed by the show on map link in the search tile overline"
          values={{
            middleDotLabel,
            showOnMapLabel,
            separator: (chunks: React.ReactNode) => <span key="map-separator" style={{ marginInline: rem(4) }}>{chunks}</span>,
            link: (chunks: React.ReactNode) => <TextLink
              weight="regular"
              onClick={mapViewClick}
              role="link"
              data-href={mapUrl}
              aria-label={showOnMapLabel}
            >
              {chunks}
            </TextLink>,
          }}
        />
      )}
    </BodyText>
    {showDistanceLabels && distanceLabels.length > 0 && <Tooltip
      description={intl.formatMessage({
        id: 'C5fCtylAHF-mqnZf5ehv',
        defaultMessage: 'This distance is calculated as a straight line on a map. Actual travel distance may vary.',
        description: 'Tooltip explaining how the distance to search target is calculated',
      })}
    >
      <CaptionTextBlock
        variant="medium"
        colour="light-foreground-alt"
        startIcon={<LineMapMarkerIcon />}
        data-testid="search-tile-distances"
      >
        {distanceLabels.join(distanceSeparator)}
      </CaptionTextBlock>
    </Tooltip>}
  </>
}

export default SearchTileRowOverlineLocation
